// Create a new file: /components/workflow/CustomAgentNodeModal.jsx
import React, { useState, useMemo, useRef } from 'react';
import { Bot, Edit } from 'lucide-react';
import toast from 'react-hot-toast';

import SchemaViewer from '../NewWorkflow/shared/SchemaViewer';
import PromptEditor from '../NewWorkflow/shared/PromptEditor';
import SchemaBuilderModal from '../NewWorkflow/modals/SchemaBuilderModal';
import {
  fieldsToSchema,
  schemaToFields,
} from '../NewWorkflow/utils/schemaUtils';

const tiptapToBackendFormat = (json) => {
  if (!json || !json.content) return [];
  const result = [];
  json.content.forEach((paragraph) => {
    if (paragraph.content) {
      paragraph.content.forEach((node) => {
        if (node.type === 'text') {
          result.push({ type: 'text', content: node.text });
        } else if (node.type === 'variablePill') {
          result.push({
            type: 'variable',
            path: node.attrs.path.split('.'),
            display: node.attrs.display,
            // ⭐ ADD THIS: Include dataType in the backend format
            dataType: node.attrs.dataType,
          });
        }
      });
    }
    // After each paragraph, add a newline. This preserves line breaks.
    result.push({ type: 'text', content: '\n' });
  });
  // Remove the last newline if it's extraneous
  if (result.length > 0 && result[result.length - 1].content === '\n') {
    result.pop();
  }
  return result;
};

const CustomAgentNodeModal = ({
  mode,
  node,
  nodes,
  edges,
  onSave,
  onDelete,
  onClose,
  globalState,
}) => {
  const [systemPromptContent, setSystemPromptContent] = useState(
    node?.data?.systemPromptContent || {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'You are a helpful assistant that only responds in JSON.',
            },
          ],
        },
      ],
    },
  );
  const [userPromptContent, setUserPromptContent] = useState(
    node?.data?.userPromptContent || {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    },
  );
  const [outputFields, setOutputFields] = useState(
    node?.data?.outputFields || [],
  );
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const lastActiveEditorRef = useRef(null);

  const inputRootFields = useMemo(() => {
    const connectedInputNodes = edges
      .filter((edge) => edge.target === node.id)
      .map((edge) => nodes.find((n) => n.id === edge.source))
      .filter(Boolean)
      .filter((parentNode) => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
      .map((parentNode) => {
        let schema;
        if (
          parentNode.data.jsonOutput &&
          parentNode.data.jsonOutputSchema?.properties
        ) {
          schema = parentNode.data.jsonOutputSchema;
        } else {
          schema = {
            title: 'Simple Output',
            type: 'object',
            properties: {
              output: {
                type: 'string',
                description: `Output from "${parentNode.data.label || parentNode.type}"`,
              },
            },
          };
        }
        return {
          id: `input_${parentNode.id}`,
          fieldName: parentNode.data.label || 'Input Node',
          dataType: 'Object',
          children: schemaToFields(schema, ''),
        };
      })
      .filter(Boolean);

    // ⭐ 2. Create a "virtual node" for the Global State if it exists
    const globalStateNode = [];
    if (globalState && globalState.fields.length > 0) {
      const globalSchema = fieldsToSchema(globalState.fields, 'globalState');
      globalStateNode.push({
        id: 'globalState', // Special, reserved ID
        fieldName: 'Global State',
        dataType: 'Object',
        children: schemaToFields(globalSchema, ''),
      });
    }

    // ⭐ 3. Combine the Global State with the other input nodes
    return [...globalStateNode, ...connectedInputNodes];
    // eslint-disable-next-line
  }, [node.id, nodes, edges]);

  // ... (rest of the logic from your sandbox AgentNodeModal: outputRootFields, handleFieldSelect, handleSchemaSave)

  const outputRootFields = useMemo(
    () => [
      {
        id: 'output',
        fieldName: 'Output',
        dataType: 'Object',
        children: outputFields,
      },
    ],
    [outputFields],
  );

  const handleFieldSelect = (fullPath, field) => {
    const rootField = inputRootFields.find((rf) => fullPath.startsWith(rf.id));
    let displayPath = fullPath;
    if (rootField) {
      displayPath =
        fullPath === rootField.id
          ? rootField.fieldName
          : fullPath
              .replace(rootField.id, rootField.fieldName)
              .replace(/^\./, '');
    }

    const variableToInsert = {
      type: 'variablePill',
      attrs: {
        path: fullPath,
        display: displayPath,
        // ⭐ ADD THIS: Pass the field's dataType
        dataType: field.dataType,
      },
    };

    const targetEditor = lastActiveEditorRef.current;

    if (targetEditor) {
      targetEditor.chain().focus().insertContent(variableToInsert).run();
    } else {
      toast.error(
        'Please click into a prompt editor first to insert a variable.',
      );
    }
  };

  const handleSchemaSave = ({ fields }) => {
    setOutputFields(fields);
  };

  const handleSaveAndClose = () => {
    const finalOutputSchema = fieldsToSchema(outputFields, 'AgentOutput');
    onSave({
      ...node,
      data: {
        ...node.data,
        label: `Custom Agent (${outputFields.length} outputs)`,
        systemPromptContent,
        userPromptContent,
        systemPrompt: tiptapToBackendFormat(systemPromptContent),
        userPrompt: tiptapToBackendFormat(userPromptContent),
        outputFields,
        jsonOutputSchema: finalOutputSchema,
        jsonOutput: true,
      },
    });
    onClose();
  };

  return (
    <>
      {/* eslint-disable-next-line */}
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-0 sm:p-4"
        onClick={onClose}
      >
        {/* eslint-disable-next-line */}
        <div
          className="flex h-full w-full flex-col border border-zinc-700 bg-zinc-900 shadow-2xl sm:h-[90vh] sm:w-[95vw] sm:rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* MODAL HEADER */}
          <div className="flex flex-shrink-0 items-center justify-between border-b border-zinc-700 p-4">
            <h2 className="flex items-center gap-3 text-xl font-semibold text-white">
              <Bot className="text-cyan-400" />
              <span>Configure Custom Agent</span>
            </h2>
            <button
              onClick={onClose}
              className="text-2xl leading-none text-zinc-400 transition-colors hover:text-white"
            >
              &times;
            </button>
          </div>

          {/* MAIN WORKSPACE */}
          <div className="grid flex-grow grid-cols-5 gap-6 overflow-hidden p-6">
            {/* --- LEFT COLUMN (Vertically Split) --- */}
            <div className="col-span-2 flex h-full flex-col gap-6 overflow-hidden">
              {/* Top Part: Available Inputs */}
              <div
                className="flex min-h-0 flex-col gap-3"
                style={{ flexBasis: '60%' }}
              >
                <div className="flex-shrink-0 px-1">
                  <h4 className="text-sm font-semibold text-slate-100">
                    Available Inputs
                  </h4>
                  {/* <p className="text-sm text-slate-400 mt-1">Click a field to insert it into an active prompt.</p> */}
                </div>
                <div className="-mr-2 flex-grow overflow-y-auto pr-2">
                  <SchemaViewer
                    rootFields={inputRootFields}
                    onFieldClick={handleFieldSelect}
                  />
                </div>
              </div>

              {/* Bottom Part: Output Schema */}
              <div
                className="flex min-h-0 flex-col gap-3"
                style={{ flexBasis: '40%' }}
              >
                <div className="flex flex-shrink-0 items-center justify-between px-1">
                  <h4 className="text-sm font-semibold text-slate-100">
                    Output Schema
                  </h4>
                  <button
                    onClick={() => setIsBuilderOpen(true)}
                    className="flex items-center gap-2 text-sm font-semibold text-indigo-400 hover:text-indigo-300"
                  >
                    <Edit size={14} />
                    <span>Edit</span>
                  </button>
                </div>
                <div className="-mr-2 flex-grow overflow-y-auto pr-2">
                  <SchemaViewer rootFields={outputRootFields} isOutput={true} />
                </div>
              </div>
            </div>

            {/* --- RIGHT COLUMN: Prompt Editors --- */}
            {/* eslint-disable-next-line */}
            <div className="col-span-3 flex h-full flex-col gap-6 overflow-y-auto pr-2">
              <div>
                <label
                  htmlFor="system-prompt"
                  className="mb-2 block text-base font-medium text-slate-200"
                >
                  System Prompt
                </label>
                <PromptEditor
                  initialContent={systemPromptContent}
                  onUpdate={setSystemPromptContent}
                  onFocus={(editor) => {
                    lastActiveEditorRef.current = editor;
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="user-prompt"
                  className="mb-2 block text-base font-medium text-slate-200"
                >
                  User Prompt
                </label>
                <PromptEditor
                  initialContent={userPromptContent}
                  onUpdate={setUserPromptContent}
                  onFocus={(editor) => {
                    lastActiveEditorRef.current = editor;
                  }}
                />
              </div>
            </div>
          </div>

          {/* MODAL FOOTER */}
          <div className="flex flex-shrink-0 items-center justify-between rounded-b-lg border-t border-zinc-700 bg-zinc-800/50 p-4">
            {mode === 'edit' && node?.id && (
              <button
                onClick={() => onDelete(node.id)}
                className="rounded-md bg-red-900/50 px-4 py-2 text-sm font-semibold text-red-400 transition-colors hover:bg-red-900/80"
              >
                Delete Node
              </button>
            )}
            <div className="flex-grow" />
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="rounded-md bg-zinc-700 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-zinc-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAndClose}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <SchemaBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onSave={handleSchemaSave}
        initialFields={outputFields}
      />
    </>
  );
};
export default CustomAgentNodeModal;
